import { Box, Typography, Stack, styled, alpha } from "@mui/material";
import { styled as muiStyled } from "@mui/material/styles";
import { ReactComponent as CheckIcon } from "assets/images/bitcoiner/check.svg";
import { ReactComponent as CrossIcon } from "assets/images/bitcoiner/cross.svg";
import { fontWeight } from "config/Theme/options";
import { useMUIThemeModeContext } from "context/MUIThemeModeContext";
import useResponsiveFont from "hooks/useResponsiveFont";
import { getAnyByMode } from "utils";

import { OptionStatus } from "..";

const pulse = {
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "10%": {
      transform: "scale(1.3)",
      opacity: 1,
    },
    "20%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(1)",
      opacity: 1,
    },
  },
};

const AnimatedCheck = muiStyled(CheckIcon)(({ theme }) => ({
  width: 24,
  height: 24,
  animation: "pulse 3s ease-in-out infinite",
  ...pulse,
}));

const shake = {
  "@keyframes shake": {
    "0%": { transform: "translateX(0)" },
    "20%": { transform: "translateX(-3px)" },
    "40%": { transform: "translateX(3px)" },
    "60%": { transform: "translateX(-3px)" },
    "80%": { transform: "translateX(3px)" },
    "100%": { transform: "translateX(0)" },
  },
};

const AnimatedCross = muiStyled(CrossIcon)(({ theme }) => ({
  width: 20,
  height: 20,
  animation: "shake 0.5s cubic-bezier(.36,.07,.19,.97) both",
  ...shake,
}));

const getHoverStyles = (color: string) => ({
  border: `1px solid ${color}`,
  backgroundColor: alpha(color, 0.01),
  color: color,
  "& .letter": {
    color: color,
  },
});

const Option = styled(Box, {
  shouldForwardProp: (prop) => prop !== "status" && prop !== "disabled",
})<{
  status: OptionStatus;
  disabled?: boolean;
}>(({ theme, status, disabled }) => {
  const { colors } = useMUIThemeModeContext();
  const baseBorderColor = alpha(colors.deepMidnightBlue, getAnyByMode(theme, 0.1, 0.4));

  const commonStyles = {
    pointerEvents: disabled ? ("none" as const) : ("auto" as const),
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "border 0.2s, background-color 0.2s, color 0.2s",
    border: `1px solid ${baseBorderColor}`,
    borderRadius: "12px",
    padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
    color: theme.palette.text.primary,
    outline: "none",
    WebkitTapHighlightColor: "transparent",
    "& .letter": {
      color: colors.gray400,
    },
    "&:hover": getHoverStyles(theme.palette.primary.light),
    "&:focus": {
      outline: "none",
      boxShadow: "none",
    },
  };

  switch (status) {
    case "selected":
      return {
        ...commonStyles,
        border: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.main,
        "& .letter": { color: theme.palette.primary.main },
        "&:hover": {
          border: `1px solid ${theme.palette.primary.main}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          color: theme.palette.primary.main,
        },
      };
    case "correct":
      return {
        ...commonStyles,
        border: `1px solid ${colors.success}`,
        backgroundColor: colors.success,
        color: "white",
        boxShadow: theme.shadows[5],
        "& .letter": { color: "white" },
      };
    case "incorrect":
      return {
        ...commonStyles,
        border: `1px solid ${colors.error500}`,
        backgroundColor: colors.error500,
        color: "white",
        boxShadow: theme.shadows[5],
        "& .letter": { color: "white" },
      };
    default:
      return commonStyles;
  }
});

export default function QuizOption({
  letter,
  text,
  status,
  disabled,
  onClick,
}: {
  letter: string;
  text: string;
  status: OptionStatus;
  disabled: boolean;
  onClick: () => void;
}) {
  const { getResponsiveRem } = useResponsiveFont();

  return (
    <Option onClick={onClick} status={status} disabled={disabled}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography className="letter" fontSize={getResponsiveRem(1.125)} fontFamily={"Nunito"} fontWeight={fontWeight.medium}>
            {letter})
          </Typography>
          <Typography fontSize={getResponsiveRem(1.25)} fontFamily={"Nunito"} fontWeight={fontWeight.medium}>
            {text}
          </Typography>
        </Stack>
        {status === "correct" && <AnimatedCheck />}
        {status === "incorrect" && <AnimatedCross />}
      </Stack>
    </Option>
  );
}
